import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { PortalApi, UploadRow } from './portalApi';
import { portalErrorMessage } from './portalHttp';
import { usePortalSession } from './PortalSession';
import { formatDateTime } from '../utils/dateUtils';
import { humanizeEnum } from '../utils/format';

// ---- P1-B customer portal ----
const PortalUploads: React.FC = () => {
  const { me } = usePortalSession();
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try { setRows(await PortalApi.uploads()); setError(null); }
    catch (e) { setError(portalErrorMessage(e, 'Could not load your uploads')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!file) return;
    setBusy(true); setError(null); setOkMsg(null);
    try {
      await PortalApi.upload(file, note.trim());
      setOkMsg('Uploaded. Your supplier will review it.');
      setFile(null); setNote('');
      if (input.current) input.current.value = '';
      await load();
    } catch (e) {
      setError(portalErrorMessage(e, 'Upload failed'));
    } finally {
      setBusy(false);
    }
  };

  const uploadsOn = me?.uploadsEnabled !== false;
  return (
    <Box>
      <Typography variant="h5" gutterBottom>Uploads</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {okMsg && <Alert severity="success" sx={{ mb: 2 }}>{okMsg}</Alert>}
      {uploadsOn ? (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>Send your supplier a document (PDF, PNG or JPEG, up to 5 MB), for example a remittance advice.</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Box sx={{ flex: '1 1 240px', maxWidth: 360 }}>
              <input ref={input} type="file" aria-label="File" accept="application/pdf,image/png,image/jpeg"
                onChange={e => setFile(e.target.files?.[0] ?? null)} />
            </Box>
            <TextField size="small" label="Note" value={note} inputProps={{ maxLength: 500 }}
              sx={{ flex: '1 1 240px', maxWidth: 420 }} onChange={e => setNote(e.target.value)} />
            <Button variant="contained" disabled={!file || busy} onClick={submit}>Upload</Button>
          </Box>
        </Paper>
      ) : <Alert severity="info" sx={{ mb: 2 }}>Uploads are switched off by your supplier.</Alert>}
      {loading ? <CircularProgress /> : (
        <Paper><TableContainer><Table size="small">
          <TableHead><TableRow><TableCell>File</TableCell><TableCell>Note</TableCell><TableCell>Uploaded</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={4} align="center">No uploads yet.</TableCell></TableRow>}
            {rows.map(r => (
              <TableRow key={r.id}>
                <TableCell>{r.originalName}</TableCell><TableCell>{r.note ?? ''}</TableCell>
                <TableCell>{formatDateTime(r.uploadedAt)}</TableCell>
                <TableCell><Chip size="small" label={humanizeEnum(r.status)} color={r.status === 'REVIEWED' ? 'success' : 'default'} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table></TableContainer></Paper>
      )}
    </Box>
  );
};

export default PortalUploads;
// ---- end P1-B ----
